import React, { useRef } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { Paperclip, SendFill, StopFill, X, FileEarmark, Film, Mic } from 'react-bootstrap-icons';
import clsx from 'clsx';

export default function Composer({
  input,
  setInput,
  pendingAttachments,
  onRemoveAttachment,
  onAttachFiles,
  onSubmit,
  onStop,
  isLoading,
  isStreaming,
  onPasteFiles,
  onOpenLive,
}) {
  const fileInputRef = useRef(null);
  const canSend = (input.trim() || pendingAttachments.length > 0) && !isLoading;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend || isStreaming) {
        if (isStreaming) onStop();
        else onSubmit(e);
      }
    }
    if (e.key === 'Escape') {
      e.target.blur();
    }
  };

  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const files = items
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (files.length && onPasteFiles) {
      e.preventDefault();
      onPasteFiles(files);
    }
  };

  return (
    <div className="mx-auto w-full max-w-chat px-4 pb-4 pt-2">
      {pendingAttachments.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {pendingAttachments.map((att) => (
            <div
              key={att.id}
              className="relative flex items-center gap-2 rounded-xl border border-border-subtle bg-surface-elevated px-2 py-1.5"
            >
              {att.preview ? (
                <img
                  src={att.preview}
                  alt={att.name}
                  className="h-10 w-10 rounded-md object-cover"
                />
              ) : (
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-surface text-accent">
                  {att.mime === 'application/pdf' ? (
                    <FileEarmark size={18} />
                  ) : (
                    <Film size={18} />
                  )}
                </span>
              )}
              <span className="max-w-[120px] truncate text-xs text-text-primary">
                {att.name}
              </span>
              <button
                type="button"
                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-surface-alt text-text-muted hover:bg-danger hover:text-white"
                onClick={() => onRemoveAttachment(att.id)}
                aria-label="Remove attachment"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={onSubmit}>
        <div className="flex items-end gap-2 rounded-2xl border border-border-subtle bg-surface-elevated px-2 py-2 shadow-composer">
          <button
            type="button"
            className="mb-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-text-muted hover:bg-surface-hover hover:text-text-primary"
            onClick={() => fileInputRef.current?.click()}
            title="Attach file"
            aria-label="Attach file"
          >
            <Paperclip size={18} />
          </button>
          <button
            type="button"
            className="mb-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-text-muted hover:bg-surface-hover hover:text-text-primary"
            onClick={onOpenLive}
            title="Live voice"
            aria-label="Live voice"
            disabled={isLoading}
          >
            <Mic size={18} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,video/*"
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              e.target.value = '';
              if (files.length) onAttachFiles(files);
            }}
          />
          <TextareaAutosize
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            minRows={1}
            maxRows={8}
            placeholder="Ask anything..."
            disabled={isLoading && !isStreaming}
            className="max-h-[200px] min-h-[40px] flex-1 resize-none appearance-none border-0 bg-transparent py-2.5 text-[15px] leading-relaxed text-text-primary shadow-none outline-none ring-0 placeholder:text-text-muted focus:border-0 focus:outline-none focus:ring-0 disabled:opacity-60"
          />
          {isStreaming ? (
            <button
              type="button"
              onClick={onStop}
              className="mb-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-danger text-white hover:bg-red-700"
              title="Stop generating"
              aria-label="Stop generating"
            >
              <StopFill size={16} />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!canSend}
              className={clsx(
                'mb-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition',
                canSend
                  ? 'bg-accent text-white hover:bg-accent-hover'
                  : 'bg-surface-hover text-text-muted cursor-not-allowed'
              )}
              title="Send"
              aria-label="Send"
            >
              <SendFill size={16} />
            </button>
          )}
        </div>
      </form>
      <p className="mt-2 text-center text-[11px] text-text-muted">
        Images ≤20 MB · PDF ≤25 MB · Video ≤50 MB · SchoolCracked can make mistakes
      </p>
    </div>
  );
}
